import localforage from "localforage";

import { STICKY_REASON_KEY_PREFIX } from "~/helpers/mobile/stickyReason";
import { RECENT_DESTINATIONS_KEY } from "~/helpers/recentDestinations";
import { SW_MESSAGE_TYPE } from "~/helpers/sw/messageContract";
import postToServiceWorker from "~/helpers/sw/postToServiceWorker";

const TENANT_SCOPED_LOCAL_STORAGE_KEYS: readonly string[] = [
	"locationSelected",
	"savedValues",
	"lastPage",
	"worksuite-ai-chats",
	RECENT_DESTINATIONS_KEY
];

// gridDataKey-namespaced grid state/layouts, the MRU filter cache, and per-module Fusion menu customization.
const TENANT_SCOPED_LOCAL_STORAGE_PREFIXES: readonly string[] = [
	"gridstate-",
	"gridLayouts-",
	"gridCustomHeaders-",
	"gridlayout-active-",
	"gridmru-",
	"fusionMenu:",
	STICKY_REASON_KEY_PREFIX
];

// sessionStorage survives the switch's reload() in the same tab; these keys are identity-scoped, not UI prefs.
// SFAuth.redirectToLogout already removes loadListSessionId on the logout path for the same reason.
const TENANT_SCOPED_SESSION_STORAGE_KEYS: readonly string[] = ["loadListSessionId"];

const isTenantScopedKey = (key: string): boolean =>
	TENANT_SCOPED_LOCAL_STORAGE_KEYS.includes(key) ||
	TENANT_SCOPED_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));

const isTenantScopedSessionKey = (key: string): boolean => TENANT_SCOPED_SESSION_STORAGE_KEYS.includes(key);

// Collect before removing — removing during the Storage.key(i) walk reindexes it and skips entries.
const purgeMatchingKeys = (store: Storage, matches: (key: string) => boolean): void => {
	const keysToRemove: string[] = [];
	for (let i = 0; i < store.length; i += 1) {
		const key = store.key(i);
		if (key && matches(key)) keysToRemove.push(key);
	}
	keysToRemove.forEach((key) => store.removeItem(key));
};

export const purgePersistedStore = async (): Promise<void> => {
	postToServiceWorker({ type: SW_MESSAGE_TYPE.PURGE_READ_CACHE });
	await localforage.clear();
};

const purgeTenantScopedState = async (): Promise<void> => {
	purgeMatchingKeys(localStorage, isTenantScopedKey);
	purgeMatchingKeys(sessionStorage, isTenantScopedSessionKey);

	await purgePersistedStore();
};

export default purgeTenantScopedState;
